/**
 * Money and number formatting.
 *
 * RULES
 * 1. The API speaks integer **rial** (`*_irr`). Every value in app state stays an integer.
 * 2. Guests read **toman** (rial / 10). Conversion happens only at display time.
 * 3. No floating point arithmetic, ever — `Math.round` on a float total is a bug,
 *    not a rounding strategy. Division is done with integer remainder distribution.
 */

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;
const RIAL_PER_TOMAN = 10;

/** Converts ASCII digits in a string to Persian digits. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/** Converts Persian/Arabic-Indic digits to ASCII so input can be parsed. */
export function toLatinDigits(input: string): string {
  return input
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

/** Groups an integer with thousands separators, e.g. 4600000 -> "4,600,000". */
function group(value: number): string {
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.trunc(value)).toString();
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Rial -> toman. Rial amounts from the backend are always multiples of 10. */
export function rialToToman(irr: number): number {
  return Math.trunc(irr / RIAL_PER_TOMAN);
}

/** Toman -> rial, for building mock fixtures from menu prices. */
export function tomanToRial(toman: number): number {
  return Math.trunc(toman) * RIAL_PER_TOMAN;
}

/** "۴۶۰٬۰۰۰" — the number only, for when the unit is in adjacent markup. */
export function formatToman(irr: number): string {
  return toPersianDigits(group(rialToToman(irr))).replace(/,/g, '٬');
}

/** "۴۶۰٬۰۰۰ تومان" — number plus unit, for standalone strings and aria-labels. */
export function formatTomanWithUnit(irr: number): string {
  return `${formatToman(irr)} تومان`;
}

/** Plain Persian integer, e.g. counts and table numbers. */
export function formatCount(value: number): string {
  return toPersianDigits(group(value));
}

/** "۴٫۷" — one decimal, Persian decimal separator. Used for ratings. */
export function formatRating(value: number): string {
  return toPersianDigits(value.toFixed(1)).replace('.', '٫');
}

/** "۱۸ دقیقه" */
export function formatMinutes(minutes: number): string {
  return `${toPersianDigits(minutes)} دقیقه`;
}

/** Parses user-typed money (Persian digits, separators, "تومان") into rial. */
export function parseTomanInput(raw: string): number {
  const digits = toLatinDigits(raw).replace(/[^\d]/g, '');
  if (!digits) return 0;
  return tomanToRial(Number(digits));
}

/**
 * Splits an integer total into `people` integer parts that sum EXACTLY to the total.
 * The remainder is spread one unit at a time across the first parts, so
 * splitEqually(10001, 3) -> [3334, 3334, 3333] and never 3333.67.
 */
export function splitEqually(totalIrr: number, people: number): number[] {
  if (!Number.isFinite(totalIrr) || !Number.isFinite(people) || people <= 0) return [];
  const total = Math.trunc(totalIrr);
  const count = Math.trunc(people);
  const base = Math.trunc(total / count);
  let remainder = total - base * count;
  const parts: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    parts.push(base + extra);
  }
  return parts;
}

/**
 * Rounds each equal share DOWN to a friendly unit (default 1,000 toman = 10,000 rial)
 * and gives the leftover to the last participant, so guests see round numbers
 * while the parts still sum to the exact total.
 */
export function splitEquallyRounded(totalIrr: number, people: number, unitIrr = 10_000): number[] {
  const total = Math.trunc(totalIrr);
  const count = Math.trunc(people);
  if (count <= 0) return [];
  if (count === 1) return [total];
  const unit = Math.max(1, Math.trunc(unitIrr));
  const rough = Math.trunc(total / count);
  const rounded = Math.trunc(rough / unit) * unit;
  if (rounded <= 0) return splitEqually(total, count);
  const head = new Array(count - 1).fill(rounded) as number[];
  const tail = total - rounded * (count - 1);
  if (tail <= 0) return splitEqually(total, count);
  return [...head, tail];
}

export interface SplitValidation {
  valid: boolean;
  sumIrr: number;
  differenceIrr: number;
  /** Persian, ready to render. Empty when valid. */
  messageFa: string;
}

/** Validates custom shares against the payable total. Integer comparison only. */
export function validateCustomShares(amountsIrr: number[], payableIrr: number): SplitValidation {
  const sum = amountsIrr.reduce((acc, value) => acc + Math.trunc(value || 0), 0);
  const difference = sum - Math.trunc(payableIrr);
  if (amountsIrr.some((value) => Math.trunc(value || 0) <= 0)) {
    return {
      valid: false,
      sumIrr: sum,
      differenceIrr: difference,
      messageFa: 'سهم هر نفر باید بیشتر از صفر باشد.',
    };
  }
  if (difference === 0) {
    return { valid: true, sumIrr: sum, differenceIrr: 0, messageFa: '' };
  }
  const amount = formatTomanWithUnit(Math.abs(difference));
  return {
    valid: false,
    sumIrr: sum,
    differenceIrr: difference,
    messageFa:
      difference > 0
        ? `جمع سهم‌ها ${amount} بیشتر از مبلغ صورت‌حساب است.`
        : `${amount} از مبلغ صورت‌حساب باقی مانده است.`,
  };
}

/** Clamps a share so a participant can never pay more than what is left. */
export function clampShareToRemaining(requestedIrr: number, remainingIrr: number): number {
  const requested = Math.max(0, Math.trunc(requestedIrr));
  const remaining = Math.max(0, Math.trunc(remainingIrr));
  return Math.min(requested, remaining);
}
